import { LinkResendOutcome } from 'services/deep-link-signin';
import { isSignInLinkAddressRefused } from 'pages/login/email-sign-in-sequence';

/**
 * The one answer only the Subscribe page can get. Its prompt sends an address the
 * person typed, so the server may refuse it as malformed. The other paths that use
 * `LinkResendOutcome` send a known account address, so it stays out of that enum.
 */
export enum SubscribeOnlyResendOutcome {
  /** the server refused the typed address as malformed (HTTP 400), so nothing was sent */
  AddressRefused = 'address-refused',
}

export type SubscribeResendOutcome = LinkResendOutcome | SubscribeOnlyResendOutcome;

/**
 * Asks the server for a fresh sign-in link and says what happened, for the
 * Subscribe page's "Can't find the email? → Resend Link". Outside the component
 * so it is unit-testable without Angular, like `purchase-offer-policy.ts` next
 * door.
 *
 * The page used to send the link and announce it inside one `try`, so a send
 * that threw skipped the announcement and the user was told nothing at all
 * (df-26428200b880). Deciding the outcome first, and only then choosing the
 * message, gives the failed send a message of its own. It answers with the
 * same outcomes the account-switch path uses in `services/deep-link-signin.ts`,
 * plus `AddressRefused` for a mistyped address (df-f6e577f3369d).
 *
 * Never throws. A refused address is the person's typo, not a fault, so it is
 * not reported. Any other failed send is handed to `reportFailure` and answered
 * as `SendFailed`, so the caller always has a message to show.
 */
export async function resendSignInLinkAsync(
  send: () => Promise<{ accountAmbiguous?: boolean } | null | undefined>,
  reportFailure: (err: unknown) => void,
): Promise<SubscribeResendOutcome> {
  try {
    const result = await send();
    // several accounts on the address means the server deliberately sent nothing (#4505).
    return result?.accountAmbiguous ? LinkResendOutcome.AccountAmbiguous : LinkResendOutcome.Sent;
  } catch (err) {
    // if the server refused the typed address as malformed, say so instead of reporting it
    if (isSignInLinkAddressRefused(err)) return SubscribeOnlyResendOutcome.AddressRefused;
    reportFailure(err);
    return LinkResendOutcome.SendFailed;
  }
}
